/**
 * Web Audio Procedural Synthesizer Graph
 * Synthesizes 100% in-memory spatial audio with zero external MP3/WAV samples.
 */

import { EventBus, EventType } from '../core/EventBus';

export class AudioSystem {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private masterCompressor: DynamicsCompressorNode | null = null;
  private subBassOsc: OscillatorNode | null = null;
  private subBassGain: GainNode | null = null;
  
  private pinkNoiseBuffer: AudioBuffer | null = null;
  private brownNoiseBuffer: AudioBuffer | null = null;
  private isMuted: boolean = false;

  constructor() {
    // Lazy AudioContext initialization on first user interaction
  }

  public init(): void {
    if (this.ctx) return;

    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    this.ctx = new AudioContextClass();

    // Master Compressor Limiter
    this.masterCompressor = this.ctx.createDynamicsCompressor();
    this.masterCompressor.threshold.setValueAtTime(-12, this.ctx.currentTime);
    this.masterCompressor.knee.setValueAtTime(40, this.ctx.currentTime);
    this.masterCompressor.ratio.setValueAtTime(12, this.ctx.currentTime);
    this.masterCompressor.attack.setValueAtTime(0.003, this.ctx.currentTime);
    this.masterCompressor.release.setValueAtTime(0.25, this.ctx.currentTime);

    // Master Gain
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.setValueAtTime(0.8, this.ctx.currentTime);

    this.masterGain.connect(this.masterCompressor);
    this.masterCompressor.connect(this.ctx.destination);

    // Generate Procedural Noise Buffers
    this.pinkNoiseBuffer = this.generatePinkNoiseBuffer(this.ctx);
    this.brownNoiseBuffer = this.generateBrownNoiseBuffer(this.ctx);

    // Start 24Hz Primordial Sub-Bass Drone
    this.startSubBassDrone();

    // Subscribe to Event Bus Audio Triggers
    const bus = EventBus.getInstance();

    bus.subscribe(EventType.AUDIO_TRIGGER_SPARK, (payload) => {
      this.playSparkFoley(payload.intensity || 1.0);
    });

    bus.subscribe(EventType.AUDIO_TRIGGER_FRICTION, (payload) => {
      this.playFrictionFoley(payload.velocity || 0);
    });

    bus.subscribe(EventType.AUDIO_TRIGGER_BELL, () => {
      this.playAnvilBell();
    });

    bus.subscribe(EventType.AUDIO_TRIGGER_IMPLOSION, () => {
      this.playCyberImplosion();
    });
  }

  public resumeContext(): void {
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  public toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setValueAtTime(this.isMuted ? 0 : 0.8, this.ctx.currentTime);
    }
    return this.isMuted;
  }

  /**
   * Scene 01: High-Pass Pink Noise Spark Foley
   */
  public playSparkFoley(intensity = 1.0): void {
    if (!this.ctx || !this.pinkNoiseBuffer || !this.masterGain || this.isMuted) return;
    this.resumeContext();

    const now = this.ctx.currentTime;
    const source = this.ctx.createBufferSource();
    source.buffer = this.pinkNoiseBuffer;

    // High-pass Filter at 4500Hz
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(4500, now);

    // Exponential Decay Gain Envelope (0.08s)
    const gain = this.ctx.createGain();
    const volume = Math.min(1.0, 0.3 + intensity * 0.7);
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    source.start(now);
    source.stop(now + 0.09);
  }

  /**
   * Scene 02: Low-Pass Brown Noise Granite Friction Foley
   */
  public playFrictionFoley(velocity: number): void {
    if (!this.ctx || !this.brownNoiseBuffer || !this.masterGain || this.isMuted) return;
    this.resumeContext();

    const now = this.ctx.currentTime;
    const source = this.ctx.createBufferSource();
    source.buffer = this.brownNoiseBuffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    const cutoff = Math.min(400, 80 + velocity * 0.5);
    filter.frequency.setValueAtTime(cutoff, now);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.4, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    source.start(now);
    source.stop(now + 0.16);
  }

  /**
   * Scene 05: Anvil Bell FM Synthesizer (440Hz Carrier + 1382.6Hz Modulator)
   */
  public playAnvilBell(): void {
    if (!this.ctx || !this.masterGain || this.isMuted) return;
    this.resumeContext();

    const now = this.ctx.currentTime;

    // Carrier Oscillator
    const carrier = this.ctx.createOscillator();
    carrier.type = 'sine';
    carrier.frequency.setValueAtTime(440, now);

    // Modulator Oscillator
    const modulator = this.ctx.createOscillator();
    modulator.type = 'sine';
    modulator.frequency.setValueAtTime(1382.6, now);

    const modGain = this.ctx.createGain();
    modGain.gain.setValueAtTime(300, now);
    modGain.gain.exponentialRampToValueAtTime(0.1, now + 2.5);

    const masterBellGain = this.ctx.createGain();
    masterBellGain.gain.setValueAtTime(0.7, now);
    masterBellGain.gain.exponentialRampToValueAtTime(0.0001, now + 2.5);

    modulator.connect(modGain);
    modGain.connect(carrier.frequency);

    carrier.connect(masterBellGain);
    masterBellGain.connect(this.masterGain);

    modulator.start(now);
    carrier.start(now);
    modulator.stop(now + 2.5);
    carrier.stop(now + 2.5);
  }

  /**
   * Scene 09: Cyber Implosion & 0dB Silence Transition
   */
  public playCyberImplosion(): void {
    if (!this.ctx || !this.masterGain) return;
    this.resumeContext();

    const now = this.ctx.currentTime;

    // Pitch drop sine sweep
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(1200, now);
    osc.frequency.exponentialRampToValueAtTime(30, now + 0.8);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.8, now);
    gain.gain.linearRampToValueAtTime(0.0, now + 0.85);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 0.85);

    // 0dB Silence Mute for 2.0s
    setTimeout(() => {
      if (this.masterGain && this.ctx) {
        this.masterGain.gain.setValueAtTime(0.0, this.ctx.currentTime);
        setTimeout(() => {
          if (this.masterGain && this.ctx && !this.isMuted) {
            this.masterGain.gain.setValueAtTime(0.8, this.ctx.currentTime);
          }
        }, 2000);
      }
    }, 850);
  }

  private startSubBassDrone(): void {
    if (!this.ctx || !this.masterGain) return;

    this.subBassOsc = this.ctx.createOscillator();
    this.subBassOsc.type = 'sine';
    this.subBassOsc.frequency.setValueAtTime(24, this.ctx.currentTime); // 24Hz sub-bass

    this.subBassGain = this.ctx.createGain();
    this.subBassGain.gain.setValueAtTime(0.15, this.ctx.currentTime);

    this.subBassOsc.connect(this.subBassGain);
    this.subBassGain.connect(this.masterGain);

    this.subBassOsc.start();
  }

  private generatePinkNoiseBuffer(ctx: AudioContext): AudioBuffer {
    const bufferSize = ctx.sampleRate * 2; // 2 sec buffer
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;

    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.0168980;
      data[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
      data[i] *= 0.11; // scale volume
      b6 = white * 0.115926;
    }
    return buffer;
  }

  private generateBrownNoiseBuffer(ctx: AudioContext): AudioBuffer {
    const bufferSize = ctx.sampleRate * 2;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    let lastOutput = 0.0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      data[i] = (lastOutput + (0.02 * white)) / 1.02;
      lastOutput = data[i];
      data[i] *= 3.5; // scale volume
    }
    return buffer;
  }
}
